import { DEFAULT_PAGINATION } from '@/constants/api';
import {
  useAssignServiceTicketToRouteMutation,
  useGetRoutesQuery,
} from '@/store/api/services/operations/routes/index';
import { useGetUsersAthenaQuery } from '@/store/api/services/users/users';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

interface AssignRouteServiceTicketProps extends ModalChildrenProps {
  service_ticket_id?: string;
}

const AssignRouteServiceTicket = ({ hide, props }: AssignRouteServiceTicketProps) => {
  const [assignServiceTicketToRoute, { isLoading }] = useAssignServiceTicketToRouteMutation();
  const [formData, setFormData] = useState({
    assigned_route: 'Select Route',
  });

  // Fetch available routes
  const { data: routesData, isLoading: isRoutesLoading } = useGetRoutesQuery({
    pagination: {
      current_page: DEFAULT_PAGINATION.current_page,
      items_per_page: DEFAULT_PAGINATION.items_per_page,
      sort_ascending: true,
      sort_by: 'created_at',
    },
    filter: {
      operands: [],
    },
  });

  // Fetch technicians for name lookup
  const { data: techniciansData } = useGetUsersAthenaQuery({ role_id: 5 });

  // Create technicians map for quick lookup
  const techniciansMap = useMemo(() => {
    const map = new Map<
      string | number,
      { first_name?: string; last_name?: string; email?: string }
    >();
    if (techniciansData?.data) {
      techniciansData.data.forEach((technician) => {
        if (technician.id) {
          map.set(technician.id, {
            first_name: technician.first_name,
            last_name: technician.last_name,
            email: technician.email,
          });
        }
      });
    }
    return map;
  }, [techniciansData]);

  // Helper function to format route status
  const formatRouteStatus = (status: string) => {
    if (!status) return '';
    return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  };

  // Helper function to get technician name
  const getTechnicianName = (technicianId: string | number | null | undefined) => {
    if (!technicianId) return 'Unassigned';
    const technician = techniciansMap.get(technicianId);
    if (technician) {
      if (technician.first_name && technician.last_name) {
        return `${technician.first_name} ${technician.last_name}`;
      }
      return technician.first_name || technician.last_name || technician.email || 'Unknown';
    }
    return 'Unassigned';
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!props?.service_ticket_id) {
      toast.error('Service ticket ID is required');
      return;
    }

    if (formData.assigned_route === 'Select Route') {
      toast.error('Please select a route');
      return;
    }

    try {
      await assignServiceTicketToRoute({
        route_id: formData.assigned_route,
        service_ticket_id: props.service_ticket_id,
      }).unwrap();

      toast.success('Service ticket assigned to route successfully');
      hide();
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message || error?.data?.message || 'Failed to assign service ticket to route';
      toast.error(errorMessage);
      console.error('Error assigning service ticket to route:', error);
    }
  };

  return (
    <>
      <DialogContent sx={{ my: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Please select a route to assign to this ticket
        </Typography>

        <FormGroup sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Assigned Route</InputLabel>
            <Select
              variant="outlined"
              value={formData.assigned_route}
              onChange={(e) => handleInputChange('assigned_route', e.target.value)}
              disabled={isRoutesLoading}
            >
              <MenuItem value="Select Route">Select Route</MenuItem>
              {routesData?.data?.map((route) => (
                <MenuItem key={route.id} value={route.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {route.name || `Route #${route.display_id || route.id}`}
                    </Typography>
                    <Chip
                      label={formatRouteStatus(route.route_status)}
                      size="small"
                      color={
                        route.route_status === 'active'
                          ? 'success'
                          : route.route_status === 'in_progress'
                            ? 'info'
                            : route.route_status === 'planning'
                              ? 'warning'
                              : 'default'
                      }
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormGroup>

        {formData.assigned_route !== 'Select Route' && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Route Details
            </Typography>
            {(() => {
              const selectedRoute = routesData?.data?.find(
                (route) => route.id === formData.assigned_route,
              );
              if (!selectedRoute) return null;

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2">
                    <strong>Technician:</strong> {getTechnicianName(selectedRoute.technician_id)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong>
                    <Chip
                      label={formatRouteStatus(selectedRoute.route_status)}
                      size="small"
                      color={
                        selectedRoute.route_status === 'active'
                          ? 'success'
                          : selectedRoute.route_status === 'in_progress'
                            ? 'info'
                            : selectedRoute.route_status === 'planning'
                              ? 'warning'
                              : 'default'
                      }
                      sx={{ ml: 1, fontWeight: 500 }}
                    />
                  </Typography>
                  <Typography variant="body2">
                    <strong>Progress:</strong>
                    {/* {selectedRoute.completed_tickets}/{selectedRoute.total_tickets}  */}
                    &nbsp;0/0 tickets
                  </Typography>
                  <Typography variant="body2">
                    <strong>Scheduled:</strong>{' '}
                    {selectedRoute.scheduled_at
                      ? new Date(selectedRoute.scheduled_at).toLocaleString()
                      : 'Not scheduled'}
                  </Typography>
                  {selectedRoute.notes && (
                    <Typography variant="body2">
                      <strong>Notes:</strong> {selectedRoute.notes}
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button variant="text" onClick={hide}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={formData.assigned_route === 'Select Route' || isLoading}
        >
          {isLoading ? 'Assigning...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </>
  );
};

export default AssignRouteServiceTicket;
